import { Flex, IconButton, Input } from '@chakra-ui/react'
import { motion } from 'framer-motion'
import { FC, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { FaSearch, FaTimes } from 'react-icons/fa'

import { useColorModeValue } from '@/components/ui/color-mode'
import { InputGroup } from '@/components/ui/input-group'
import { useSearch } from '@/contexts/Circles/SearchContext'
import { PoolingRoutes } from '@/routes/_hooks/routes.enum'
import { useNavigate } from '@/routing'

type SearchBarProps = {
  onFocus?: () => void
  onBlur?: () => void
  inputRef?: React.Ref<HTMLInputElement>
}

export type SearchInputFormValues = {
  searchTerms: string
}

export const SearchBar: FC<SearchBarProps> = ({
  onFocus,
  onBlur,
  inputRef,
}) => {
  const {
    tempSearchTerms,
    setTempSearchTerms,
    handleSubmit: onSubmit,
  } = useSearch()
  const { t } = useTranslation()

  const { register, handleSubmit } = useForm<SearchInputFormValues>({
    defaultValues: {
      searchTerms: tempSearchTerms,
    },
  })
  const navigate = useNavigate()

  const [isFocused, setIsFocused] = useState(false)

  const handleSearch = (data: SearchInputFormValues) => {
    setTempSearchTerms(data.searchTerms)
    onSubmit()
  }

  const handleReset = () => {
    setTempSearchTerms('')
    navigate(PoolingRoutes.Search)
  }

  const { ref, ...rest } = register('searchTerms')

  return (
    <Flex width="100%" px={{ base: 0, md: 3 }}>
      {/* Width is fluid (100% of the container) so it never overflows narrow
          mobile viewports; `maxWidth` animates the desktop collapse/expand. */}
      <motion.div
        initial={{ maxWidth: '300px' }}
        animate={{ maxWidth: isFocused ? '100%' : '300px' }}
        transition={{ duration: 0.4, ease: 'easeInOut' }}
        style={{ overflow: 'hidden', width: '100%' }}
      >
        <form onSubmit={handleSubmit(handleSearch)}>
          <InputGroup
            w="99%"
            endElement={
              <Flex height="100%" alignItems="center">
                {tempSearchTerms && (
                  <IconButton
                    aria-label="Reset"
                    variant="ghost"
                    size="sm"
                    onClick={handleReset}
                  >
                    <FaTimes />
                  </IconButton>
                )}
                <IconButton
                  aria-label="Search"
                  type="submit"
                  size="sm"
                  rounded="full"
                >
                  <FaSearch />
                </IconButton>
              </Flex>
            }
          >
            <Input
              aria-label={t('pooling.index.search.placeholder')}
              placeholder={t('pooling.index.search.placeholder')}
              size="xl"
              w="100%"
              bg={useColorModeValue('white', 'gray.700')}
              rounded="full"
              color={useColorModeValue('gray.700', 'white')}
              py={2}
              ref={inputRef}
              {...rest}
              value={tempSearchTerms}
              onChange={(e) => setTempSearchTerms(e.target.value)}
              onFocus={() => {
                if (onFocus) {
                  onFocus()
                }
                setIsFocused(true)
              }}
              onBlur={() => {
                if (onBlur) {
                  onBlur()
                }
                setIsFocused(false)
              }}
            />
          </InputGroup>
        </form>
      </motion.div>
    </Flex>
  )
}
